import { memo, type RefObject } from 'react';

import { ImageSearchIcon } from '@air/next-icons';
import { cn } from '@air/utils-tailwind';

import { type DropZone } from '~/components/PrivateDropzone/types';

export type ImageSearchZoneOverlayProps = {
  hoveredZone: DropZone | null;
  dropZoneSearchByImageRef: RefObject<HTMLDivElement>;
  showSearchByImageOverlay: boolean;
  searchCommandTriggerPosition: DOMRect | null;
  dropzoneDescription: string;
};

export const ImageSearchZoneOverlay = memo(
  ({
    hoveredZone,
    dropZoneSearchByImageRef,
    showSearchByImageOverlay,
    searchCommandTriggerPosition,
    dropzoneDescription,
  }: ImageSearchZoneOverlayProps) => {
    if (!searchCommandTriggerPosition) {
      return null;
    }

    return (
      <div
        ref={dropZoneSearchByImageRef}
        style={{
          top: searchCommandTriggerPosition.top,
          left: searchCommandTriggerPosition.left,
          width: searchCommandTriggerPosition.width,
        }}
        className={cn(
          'absolute z-1 flex flex-col gap-1 transition-opacity',
          showSearchByImageOverlay ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        {/* Purpose of element: overlay a "selected shadow" over the search command trigger */}
        <div
          className="rounded-8 bg-transparent shadow-search-command"
          style={{
            width: searchCommandTriggerPosition.width,
            height: searchCommandTriggerPosition.height,
          }}
        ></div>
        <div
          data-testid="SEARCH_BY_IMAGE_OVERLAY"
          className="rounded-8 border border-grey-6 bg-grey-1 shadow-search-command"
        >
          <div className="flex flex-col gap-1 px-4 py-3">
            <div className="px-1 py-0.5 font-bold text-10 text-grey-9 uppercase tracking-[0.4px]">Search by image</div>
            <div
              className={cn(
                'flex min-h-[184px] items-center justify-center rounded-8 border border-grey-7 border-dashed px-4 py-8 transition-colors',
                hoveredZone === 'search-by-image' &&
                  'border-blue-9 border-solid bg-blue-1 outline outline-1 outline-blue-9',
              )}
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <ImageSearchIcon className="size-6 text-grey-12" />
                <p className="font-medium text-14 text-grey-12">
                  {hoveredZone === 'search-by-image' ? 'Drop here to search' : 'Drag and drop image here to search'}
                </p>
                <p className="text-12 text-grey-10">{dropzoneDescription}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ImageSearchZoneOverlay.displayName = 'ImageSearchZoneOverlay';
